import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { getProgramUsageStats } from '../../services/purchaseProgramApi';

function KpiCard({ label, value, sub, color = '#1a56db' }) {
  return (
    <div className="card" style={{ borderRadius: 10, border: '1px solid #e5e7eb', padding: '18px 20px' }}>
      <p style={{ fontSize: 13, color: '#6b7280', margin: '0 0 4px' }}>{label}</p>
      <p style={{ fontSize: 26, fontWeight: 700, margin: '0 0 2px', color }}>{value ?? '—'}</p>
      {sub && <p style={{ fontSize: 12, color: '#9ca3af', margin: 0 }}>{sub}</p>}
    </div>
  );
}

function EmptyChart() {
  return <div style={{ textAlign: 'center', padding: '32px 0', color: '#9ca3af', fontSize: 13 }}>No data yet for this period</div>;
}

export default function AnalyticsTab({ programs }) {
  const [searchParams] = useSearchParams();
  const [selectedId, setSelectedId] = useState(() => searchParams.get('programId') || '');
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Auto-select first program if nothing pre-selected from URL
  useEffect(() => {
    if (programs.length > 0 && !selectedId) setSelectedId(String(programs[0].id));
  }, [programs, selectedId]);

  useEffect(() => {
    if (!selectedId) return;
    setLoading(true);
    setError('');
    getProgramUsageStats(Number(selectedId))
      .then(setStats)
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  }, [selectedId]);

  const fmt$ = (v) => v == null ? '—' : `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const fmtPct = (v) => v == null ? '—' : `${v}%`;

  const donutData = stats?.amount_limit
    ? [
        { name: 'Used', value: stats.amount_used },
        { name: 'Remaining', value: Math.max(0, stats.amount_limit - stats.amount_used) },
      ]
    : [];

  return (
    <div>
      {/* Program selector */}
      <div className="d-flex align-items-center gap-3 mb-4 flex-wrap">
        <label htmlFor="analytics-prog" className="fw-semibold" style={{ whiteSpace: 'nowrap' }}>Program:</label>
        <select
          id="analytics-prog"
          className="form-select"
          style={{ maxWidth: 320 }}
          value={selectedId}
          onChange={e => setSelectedId(e.target.value)}
        >
          <option value="">Select a program…</option>
          {programs.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
        {loading && <div className="spinner-border spinner-border-sm text-primary" role="status"><span className="visually-hidden">Loading…</span></div>}
      </div>

      {error && (
        <div className="alert alert-danger" role="alert">{error}</div>
      )}

      {!selectedId && (
        <div style={{ textAlign: 'center', padding: '48px 0', color: '#9ca3af' }}>Select a program to view analytics.</div>
      )}

      {stats && !loading && (
        <>
          {/* KPI row */}
          <div className="row g-3 mb-4">
            <div className="col-sm-6 col-xl-3">
              <KpiCard label="Total Spend" value={fmt$(stats.amount_used)} sub={`of ${fmt$(stats.amount_limit)} limit`} color="#1a56db" />
            </div>
            <div className="col-sm-6 col-xl-3">
              <KpiCard label="Budget Utilization" value={fmtPct(stats.utilization_pct)} sub="of program budget" color={stats.utilization_pct >= 90 ? '#c81e1e' : stats.utilization_pct >= 70 ? '#b45309' : '#057a55'} />
            </div>
            <div className="col-sm-6 col-xl-3">
              <KpiCard label="Unique Users" value={stats.unique_users} sub="in this period" color="#6366f1" />
            </div>
            <div className="col-sm-6 col-xl-3">
              <KpiCard label="Avg Spend / User" value={fmt$(stats.avg_spend_per_user)} sub="this period" color="#057a55" />
            </div>
          </div>

          {/* Row 1: Donut + Area chart */}
          <div className="row g-3 mb-4">
            <div className="col-md-4">
              <div className="card" style={{ borderRadius: 10, border: '1px solid #e5e7eb', padding: '16px' }}>
                <h4 style={{ fontSize: 14, fontWeight: 700, marginBottom: 12, color: '#374151' }}>Budget Utilization</h4>
                {donutData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={200}>
                    <PieChart>
                      <Pie data={donutData} cx="50%" cy="50%" innerRadius={55} outerRadius={80} dataKey="value">
                        <Cell fill="#1a56db" />
                        <Cell fill="#e5e7eb" />
                      </Pie>
                      <Tooltip formatter={(v) => fmt$(v)} />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                ) : <EmptyChart />}
              </div>
            </div>
            <div className="col-md-8">
              <div className="card" style={{ borderRadius: 10, border: '1px solid #e5e7eb', padding: '16px' }}>
                <h4 style={{ fontSize: 14, fontWeight: 700, marginBottom: 12, color: '#374151' }}>Spend Over Time</h4>
                {stats.spend_over_time?.length > 0 ? (
                  <ResponsiveContainer width="100%" height={200}>
                    <AreaChart data={stats.spend_over_time}>
                      <defs>
                        <linearGradient id="spendGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#1a56db" stopOpacity={0.2} />
                          <stop offset="95%" stopColor="#1a56db" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="day" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} tickFormatter={v => `$${v}`} />
                      <Tooltip formatter={(v) => fmt$(v)} />
                      <Area type="monotone" dataKey="amount" stroke="#1a56db" fill="url(#spendGrad)" strokeWidth={2} name="Spend" />
                    </AreaChart>
                  </ResponsiveContainer>
                ) : <EmptyChart />}
              </div>
            </div>
          </div>

          {/* Row 2: Top users + Top products */}
          <div className="row g-3">
            <div className="col-md-6">
              <div className="card" style={{ borderRadius: 10, border: '1px solid #e5e7eb', padding: '16px' }}>
                <h4 style={{ fontSize: 14, fontWeight: 700, marginBottom: 12, color: '#374151' }}>Top Users by Spend</h4>
                {stats.top_users?.length > 0 ? (
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={stats.top_users} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={v => `$${v}`} />
                      <YAxis type="category" dataKey="user_id" tick={{ fontSize: 11 }} width={90} />
                      <Tooltip formatter={(v) => fmt$(v)} />
                      <Bar dataKey="amount_spent" fill="#1a56db" radius={[0, 4, 4, 0]} name="Spend" />
                    </BarChart>
                  </ResponsiveContainer>
                ) : <EmptyChart />}
              </div>
            </div>
            <div className="col-md-6">
              <div className="card" style={{ borderRadius: 10, border: '1px solid #e5e7eb', padding: '16px' }}>
                <h4 style={{ fontSize: 14, fontWeight: 700, marginBottom: 12, color: '#374151' }}>Top Products by Qty</h4>
                {stats.top_products?.length > 0 ? (
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={stats.top_products} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis type="number" tick={{ fontSize: 11 }} />
                      <YAxis type="category" dataKey="product_code" tick={{ fontSize: 11 }} width={90} />
                      <Tooltip />
                      <Bar dataKey="qty_purchased" fill="#057a55" radius={[0, 4, 4, 0]} name="Qty" />
                    </BarChart>
                  </ResponsiveContainer>
                ) : <EmptyChart />}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
